import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useRobotsMeta } from '../hooks/useRobotsMeta';

/**
 * SPA 404 page.
 *
 * Notes on the HTTP status: Vercel serves the CRA fallback (index.html) with
 * a 200 status for unknown paths, so this page renders at 200 by default.
 * We use a <meta http-equiv> hint + a client-side signal (data attr) to nudge
 * crawlers, but the durable fix requires either Vercel Edge Middleware or a
 * migration to an SSR framework. For now: noindex is the guarantee, and the
 * UI clearly communicates a not-found state.
 */
const NotFoundPage = () => {
  useRobotsMeta('noindex, nofollow');

  React.useEffect(() => {
    document.title = 'Page not found · Pixally';
  }, []);

  return (
    <div
      className="min-h-screen bg-pixally-dark-bg text-white flex items-center justify-center px-6"
      data-testid="not-found-page"
      data-http-status="404"
    >
      <div className="max-w-lg text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-pixally-gold/10 border border-pixally-gold/30 px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] font-semibold text-pixally-gold mb-8">
          Error 404
        </div>
        <h1 className="font-display text-5xl md:text-6xl font-semibold leading-tight mb-4">
          This page went off the timeline.
        </h1>
        <p className="text-lg text-gray-400 mb-10 leading-relaxed">
          The page you’re looking for doesn’t exist, was moved, or hasn’t been published yet.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full bg-pixally-gold px-6 py-3 font-semibold text-pixally-charcoal shadow-lg shadow-pixally-gold/30 hover:bg-pixally-gold-light transition-all"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Pixally
          </Link>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-semibold text-white hover:bg-white/5 transition-all"
          >
            Read the blog
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
